import { z } from 'zod';

import { UuidSchema } from './common.js';
import { PackageFacetsSchema, PublicPackageCardSchema } from './website-packages.js';

export const PublicCategoryPageThemeTabSchema = z.object({
  marketingTagId: UuidSchema,
  label: z.string(),
});
export type PublicCategoryPageThemeTab = z.infer<typeof PublicCategoryPageThemeTabSchema>;

export const PublicCategoryPageDestinationTileSchema = z.object({
  destinationPageId: UuidSchema,
  /**
   * Destinations-master id this page is bound to (ADR-0031 soft reference), or
   * null for an unbound page. This - not `slug` - is what the package search
   * filters on, so the quick-pick circles must send it as `destinationId`.
   */
  destinationId: UuidSchema.nullable(),
  slug: z.string(),
  name: z.string(),
  imageUrl: z.string().nullable(),
  href: z.string(),
  badge: z.string().nullable(),
  packageCount: z.number().int(),
  startingPrice: z.string().nullable(),
  themeTagIds: z.array(UuidSchema),
});
export type PublicCategoryPageDestinationTile = z.infer<
  typeof PublicCategoryPageDestinationTileSchema
>;

export const PublicCategoryPageFaqSchema = z.object({
  question: z.string(),
  answerHtml: z.string(),
});
export const PublicCategoryPageSeoLinkSchema = z.object({
  groupLabel: z.string(),
  label: z.string(),
  href: z.string(),
});
export const PublicCategoryPageReviewSchema = z.object({
  customerName: z.string(),
  summary: z.string(),
  rating: z.number().int(),
  portraitUrl: z.string().nullable(),
  imageUrls: z.array(z.string()),
});

export const PublicCategoryPageSchema = z.object({
  slug: z.string(),
  title: z.string(),
  heroHeading: z.string().nullable(),
  heroSubheading: z.string().nullable(),
  heroMediaUrl: z.string().nullable(),
  heroVideoUrl: z.string().nullable(),
  aboutHtml: z.string().nullable(),
  metaTitle: z.string().nullable(),
  metaDescription: z.string().nullable(),
  metaKeywords: z.string().nullable(),
  ogImageUrl: z.string().nullable(),
  /**
   * `TripType.code` this page's packages must carry, or null when the page
   * lists across every trip type. Derived from the slug server-side. Defaults
   * to null so a response from an API build predating this field still parses.
   */
  tripTypeCode: z.string().nullable().default(null),
  themeTabs: z.array(PublicCategoryPageThemeTabSchema),
  faqs: z.array(PublicCategoryPageFaqSchema),
  seoLinks: z.array(PublicCategoryPageSeoLinkSchema),
  reviews: z.array(PublicCategoryPageReviewSchema),
});
export type PublicCategoryPage = z.infer<typeof PublicCategoryPageSchema>;

export const PublicCategoryPageViewSchema = z.object({
  page: PublicCategoryPageSchema,
  facets: PackageFacetsSchema.nullable(),
  bestSellers: z.array(PublicPackageCardSchema),
  destinations: z.array(PublicCategoryPageDestinationTileSchema),
});
export type PublicCategoryPageView = z.infer<typeof PublicCategoryPageViewSchema>;
